'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart, MessageSquare, Users, Search, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackUserAction } from '@/app/actions/posthog';
import { PostHogEventName } from '@/types/posthog';
import { useOnboardingStore, type UseCase } from '../../store';
import { updateCompanyOnboarding } from '../../actions';
import { StepWrapper } from '../shared/StepWrapper';

interface GoalsStepProps {
  onNext: () => void;
  onBack: () => void;
}

const GOALS: { value: UseCase; label: string; icon: React.ReactNode }[] = [
  {
    value: 'convert_customers',
    label: 'Convert more customers',
    icon: <ShoppingCart className="h-5 w-5" />,
  },
  {
    value: 'collect_feedback',
    label: 'Collect feedback & reviews',
    icon: <MessageSquare className="h-5 w-5" />,
  },
  {
    value: 'improve_segmentation',
    label: 'Improve segmentation & personalization',
    icon: <Users className="h-5 w-5" />,
  },
  {
    value: 'product_research',
    label: 'Do product research',
    icon: <Search className="h-5 w-5" />,
  },
  {
    value: 'stand_out',
    label: 'Stand out in the inbox',
    icon: <Sparkles className="h-5 w-5" />,
  },
];

export function GoalsStep({ onNext, onBack }: GoalsStepProps) {
  const store = useOnboardingStore();
  const [selected, setSelected] = useState<UseCase[]>(store.useCases);

  const toggleGoal = (goal: UseCase) => {
    setSelected((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  };

  const handleContinue = async () => {
    store.setUseCases(selected);

    if (store.companyId) {
      updateCompanyOnboarding(store.companyId, { useCases: selected });

      trackUserAction(
        store.email,
        store.companyId,
        PostHogEventName.ONBOARDING_WIZARD_USE_CASES_SELECTED,
        { use_cases: selected }
      );
    }

    onNext();
  };

  return (
    <StepWrapper
      title="What do you want to use Kinetic for?"
      subtitle="Pick as many as you want. This helps us personalize your experience."
    >
      <div className="space-y-5">
        <div className="space-y-2.5">
          {GOALS.map((goal, index) => {
            const isSelected = selected.includes(goal.value);
            return (
              <motion.button
                key={goal.value}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06 }}
                onClick={() => toggleGoal(goal.value)}
                className={`w-full flex items-center gap-3.5 p-3.5 rounded-[13px] border text-left transition-all ${
                  isSelected
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-white border-border hover:border-kinetic-pink'
                }`}
              >
                <div
                  className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                    isSelected
                      ? 'bg-white/20 text-primary-foreground'
                      : 'bg-kinetic-light-pink text-foreground/60'
                  }`}
                >
                  {goal.icon}
                </div>
                <span className="font-medium text-sm flex-1">{goal.label}</span>
                <div
                  className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    isSelected
                      ? 'bg-white border-white'
                      : 'border-foreground/20'
                  }`}
                >
                  {isSelected && <Check className="h-3 w-3 text-primary" strokeWidth={3} />}
                </div>
              </motion.button>
            );
          })}
        </div>

        <Button
          onClick={handleContinue}
          className="w-full bg-primary hover:bg-primary/90 rounded-[13px] h-12 text-base"
          disabled={selected.length === 0}
        >
          Continue
        </Button>

        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
      </div>
    </StepWrapper>
  );
}
